import { Home as HomeIcon, Building2, Sofa, MapPinned, ArrowRight } from "lucide-react";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { Container, Overline } from "@/components/Sections";
import { Reveal } from "@/components/Reveal";
import { useQuote } from "@/components/QuoteDialog";
import { IMAGES } from "@/data/site";

const H2 = ({ children, light }) => (
  <h2 className={`font-head text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight mt-3 leading-[1.1] ${light ? "text-white" : ""}`}>{children}</h2>
);

const CHAPTERS = [
  { img: "/traslochi/inscatolamento.jpg", t: "Imballaggio e inscatolamento", d: "Avvolgiamo piatti, bicchieri e oggetti fragili in carta e pluriball, li sistemiamo in scatole robuste e segnaliamo ogni collo delicato come FRAGILE. Le scatole le forniamo noi.", tags: ["Fornitura scatole", "Protezione oggetti fragili", "Etichettatura per stanza"] },
  { img: "/traslochi/smontaggio.jpg", t: "Smontaggio dei mobili", d: "Armadi, letti, librerie e cucine vengono smontati dai nostri operatori. Ante e pannelli sono protetti con coperte imbottite e le viterie raccolte in sacchetti per ogni mobile.", tags: ["Armadi e cucine", "Viterie catalogate", "Coperte di protezione"] },
  { img: "/traslochi/carico.jpg", t: "Carico sul furgone", d: "Divani e mobili avvolti nel film protettivo, scatole sistemate e bloccate con cinghie: carichiamo il mezzo in modo che nulla si muova durante il viaggio.", tags: ["Facchinaggio", "Film protettivo", "Fissaggio con cinghie"] },
  { img: "/traslochi/montaggio.jpg", t: "Trasporto e rimontaggio", d: "Portiamo tutto a destinazione, in città o in un'altra regione, e rimontiamo i mobili nella nuova casa, stanza per stanza.", tags: ["Trasporto con mezzi AXAL", "Rimontaggio", "Consegna al piano"] },
];

export const MovingChapters = () => (
  <section className="py-24 md:py-32" data-testid="moving-chapters">
    <Container>
      <Reveal className="max-w-2xl mb-16 md:mb-24">
        <Overline>Il trasloco, fase per fase</Overline>
        <H2>Ogni dettaglio, nelle mani giuste.</H2>
      </Reveal>
      <div className="space-y-20 md:space-y-32">
        {CHAPTERS.map((c, i) => (
          <div key={c.t} className="grid lg:grid-cols-12 gap-8 lg:gap-14 items-center" data-testid={`moving-chapter-${i}`}>
            <Reveal className={`lg:col-span-7 ${i % 2 ? "lg:order-2" : ""}`}>
              <div className="relative rounded-[2rem] overflow-hidden aspect-[3/2] group">
                <img src={c.img} alt={c.t} loading="lazy" className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1.2s] group-hover:scale-105" />
                <span className="absolute top-5 left-5 px-3.5 py-1.5 rounded-full bg-black/55 backdrop-blur-md text-white text-xs font-bold tracking-wider">FASE 0{i + 1}</span>
              </div>
            </Reveal>
            <Reveal delay={0.1} className={`lg:col-span-5 ${i % 2 ? "lg:order-1" : ""}`}>
              <span className="font-head text-7xl md:text-8xl font-extrabold text-zinc-200 leading-none select-none">0{i + 1}</span>
              <h3 className="font-head text-3xl md:text-4xl font-extrabold tracking-tight -mt-6 relative">{c.t}</h3>
              <p className="text-zinc-500 mt-4 text-lg leading-relaxed">{c.d}</p>
              <div className="flex flex-wrap gap-2 mt-6">
                {c.tags.map((t) => <span key={t} className="px-3.5 py-1.5 rounded-full border border-zinc-200 text-sm font-semibold text-zinc-600">{t}</span>)}
              </div>
            </Reveal>
          </div>
        ))}
      </div>
    </Container>
  </section>
);

const TYPES = [
  { icon: HomeIcon, t: "Abitazioni", d: "Appartamenti, ville e monolocali, dal primo all'ultimo scatolone." },
  { icon: Building2, t: "Uffici e negozi", d: "Trasferimenti di uffici, studi e attività commerciali, organizzati per ridurre i tempi di fermo." },
  { icon: Sofa, t: "Singoli mobili", d: "Un divano, un armadio o un pianoforte: trasportiamo anche il singolo pezzo." },
  { icon: MapPinned, t: "Traslochi nazionali", d: "Da Roma verso tutta Italia, e da tutta Italia verso Roma." },
];

export const MovingTypes = () => (
  <section className="py-24 md:py-32 bg-[#121212] text-white relative overflow-hidden" data-testid="moving-types">
    <div className="absolute inset-0 noise-overlay opacity-[0.05]" />
    <Container className="relative">
      <Reveal className="max-w-2xl mb-14">
        <Overline>Per ogni esigenza</Overline>
        <H2 light>Grande o piccolo, lo spostiamo noi.</H2>
      </Reveal>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
        {TYPES.map((t, i) => (
          <Reveal key={t.t} delay={i * 0.08}>
            <div className="h-full rounded-2xl border border-white/10 p-7 hover:border-[#FF5A00]/60 transition-colors">
              <div className="w-12 h-12 rounded-xl bg-[#FF5A00] flex items-center justify-center"><t.icon size={22} /></div>
              <h3 className="font-head text-xl font-bold mt-5">{t.t}</h3>
              <p className="text-zinc-400 text-[15px] mt-2 leading-relaxed">{t.d}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </Container>
  </section>
);

const EXTRAS = [
  { img: IMAGES.autoscala, t: "Autoscala con operatore", d: "Per i piani alti o le scale strette, i mobili passano dalla finestra o dal balcone.", preset: "Trasloco con autoscala" },
  { img: IMAGES.deposito, t: "Deposito temporaneo", d: "La casa nuova non è ancora pronta? Custodiamo i tuoi mobili fino al giorno della consegna.", preset: "Trasloco con deposito" },
];

export const MovingExtras = () => {
  const { openQuote } = useQuote();
  return (
    <section className="py-24 md:py-32" data-testid="moving-extras">
      <Container>
        <Reveal className="mb-12">
          <Overline>Servizi aggiuntivi</Overline>
          <H2>Quando serve qualcosa in più.</H2>
        </Reveal>
        <div className="grid md:grid-cols-2 gap-5">
          {EXTRAS.map((e, i) => (
            <Reveal key={e.t} delay={i * 0.1}>
              <div className="group relative h-[420px] rounded-[1.75rem] overflow-hidden">
                <img src={e.img} alt={e.t} loading="lazy" className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent" />
                <div className="absolute bottom-0 p-7 md:p-9">
                  <h3 className="font-head text-2xl md:text-3xl font-extrabold text-white">{e.t}</h3>
                  <p className="text-white/80 mt-2 max-w-md">{e.d}</p>
                  <button onClick={() => openQuote({ servizio: "trasloco", messaggio: e.preset })} data-testid={`moving-extra-${i}`}
                    className="mt-5 h-12 px-6 rounded-full bg-[#FF5A00] text-white font-bold inline-flex items-center gap-2 hover:bg-[#E04F00] transition-colors">
                    Richiedi preventivo <ArrowRight size={18} />
                  </button>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </Container>
    </section>
  );
};

const FAQ = [
  { q: "Quanto costa un trasloco?", a: "Dipende dalla quantità di mobili e scatole, dalla distanza, dai piani e dai servizi scelti (inscatolamento, smontaggio, autoscala, deposito). Il preventivo è gratuito: scrivici o compila il modulo." },
  { q: "Fornite voi le scatole?", a: "Sì, possiamo fornire le scatole e il materiale di imballaggio, oppure occuparci direttamente dell'inscatolamento." },
  { q: "Smontate e rimontate i mobili?", a: "Sì: smontiamo armadi, letti, librerie e cucine e li rimontiamo nella nuova casa." },
  { q: "Come proteggete gli oggetti fragili?", a: "Li avvolgiamo in carta e pluriball, li mettiamo in scatole dedicate e li segnaliamo come FRAGILE per il carico e lo scarico." },
  { q: "Fate traslochi anche fuori Roma?", a: "Sì, organizziamo traslochi in tutta Italia." },
  { q: "Cosa succede se la casa nuova non è pronta?", a: "Possiamo tenere i tuoi mobili nel nostro deposito temporaneo e consegnarli quando sei pronto." },
];

export const MovingFaq = () => (
  <section className="py-24 md:py-32 bg-[#F4F4F5]" data-testid="moving-faq">
    <Container className="grid lg:grid-cols-[1fr_1.4fr] gap-12">
      <Reveal>
        <Overline>Domande frequenti</Overline>
        <H2>Prima di traslocare.</H2>
      </Reveal>
      <Reveal delay={0.1}>
        <Accordion type="single" collapsible className="border-t border-zinc-200">
          {FAQ.map((f, i) => (
            <AccordionItem key={f.q} value={`t${i}`} className="border-zinc-200" data-testid={`moving-faq-${i}`}>
              <AccordionTrigger className="font-head text-lg font-bold text-left hover:no-underline hover:text-[#FF5A00] py-5">{f.q}</AccordionTrigger>
              <AccordionContent className="text-zinc-500 text-[15px] leading-relaxed">{f.a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </Reveal>
    </Container>
  </section>
);
